import {
  type PlayerRatingResponse,
  type ProfileOrgResponse,
  type ProfileRecruitResponse,
} from '@on3/api';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { On3Avatar } from 'components/Image/On3Avatar';
import { TextLink } from 'components/UI/Link/TextLink';
import { StarRating } from 'components/UI/StarRating/StarRating';
import { Text } from 'components/UI/Typography/Text';
import useAthlete from 'contexts/AthleteProvider';
import { format } from 'date-fns';

import styles from './SecondaryRankings.module.scss';

interface PlayerRankingsProps {
  playerRankings?: PlayerRatingResponse;
  recruitData?: ProfileRecruitResponse | null;
  collegeData?: ProfileOrgResponse | null;
}

export const SecondaryRankings = ({
  playerRankings,
  recruitData,
  collegeData,
}: PlayerRankingsProps) => {
  const { athlete, athleteInHighSchool } = useAthlete();
  const { expMin, expMax, organization } = collegeData || {};
  const { asset } = organization || {};
  const {
    ranking,
    positionAbbr,
    rating,
    stars,
    stateAbbr,
    overallRank,
    stateRank,
    positionRank,
  } = playerRankings || {};

  const headerText = athleteInHighSchool
    ? 'Rankings and Ratings'
    : 'As A Recruit';

  const baseLink = `/rivals/rankings/player/${slugify(
    ranking?.sport?.name,
  )}/${ranking?.year}/`;
  const linkPosition = positionAbbr?.toLocaleLowerCase();
  const linkState = stateAbbr?.toLocaleLowerCase();
  const positionLink = `${baseLink}?position=${linkPosition}`;
  const stateLink = `${baseLink}?state=${linkState}`;

  return (
    <section className={styles.container}>
      {!athleteInHighSchool && (
        <div className={styles.collegeBlock}>
          <Text className={styles.heading} variant="h4">
            {asset && (
              <On3Avatar
                domain={asset.domain}
                height={50}
                source={asset.source}
                teamAvatar
                type={asset?.type}
                width={50}
              />
            )}

            {organization?.fullName}
          </Text>
          <ul className={styles.ratingsList}>
            <li>
              <Text className={styles.label} component="span">
                Experience
              </Text>

              <Text component="span">
                {expMin} -{' '}
                {expMax && expMax >= new Date().getFullYear()
                  ? 'present'
                  : expMax}
              </Text>
            </li>
            {athlete?.age && (
              <li>
                <Text className={styles.label} component="span">
                  Age
                </Text>
                <Text component="span">{athlete?.age}</Text>
              </li>
            )}
          </ul>
        </div>
      )}

      <Text className={styles.heading} variant="h4">
        {headerText}
      </Text>
      <ul className={styles.ratingsList}>
        <li>
          <Text className={styles.label} component="span">
            Rivals Rating
          </Text>
          <Text component="span">
            <StarRating
              hasBorder={false}
              href={baseLink}
              position={positionAbbr}
              rating={rating}
              stars={stars}
              state={stateAbbr}
            />
          </Text>
        </li>
        <li>
          <Text className={styles.label} component="span">
            Rankings
          </Text>
          <Text className={styles.rankingsBlock} component="span">
            <span>
              Natl <TextLink href={baseLink}>{overallRank}</TextLink>
            </span>
            <span>
              Pos{' '}
              {linkPosition ? (
                <TextLink href={positionLink}>{positionRank}</TextLink>
              ) : (
                positionRank
              )}
            </span>
            <span>
              St{' '}
              {linkState ? (
                <TextLink href={stateLink}>{stateRank}</TextLink>
              ) : (
                stateRank
              )}
            </span>
          </Text>
        </li>
        <li>
          <Text className={styles.label} component="span">
            Class
          </Text>
          <Text component="span">{athlete?.classYear}</Text>
        </li>
        <li>
          <Text className={styles.label} component="span">
            Committed
          </Text>
          <Text className={styles.status} component="span">
            {recruitData?.committedStatus?.committedAsset?.url && (
              <img
                alt="Committed Team"
                height="30"
                src={recruitData?.committedStatus?.committedAsset?.url}
                width="30"
              />
            )}
            {recruitData?.committedStatus?.date
              ? format(new Date(recruitData?.committedStatus?.date), 'M/dd/yy')
              : '-'}
          </Text>
        </li>
      </ul>
    </section>
  );
};
